import { useCallback, useSyncExternalStore } from 'react';

/**
 * Which side of the marketplace a QA Playground demo is being presented from (#9141, design
 * prototype "I'm the Client / I'm the Supplier"). Presentation state only: it picks the
 * default Network and onboarding-dashboard tab. It never changes what the user can see or do.
 *
 * Persisted per browser in localStorage so it survives navigation and reloads during a pitch.
 * Storage only adds persistence: the role itself always lives in memory. Once storage fails —
 * a blocked read (blocked site data) or just a blocked write (Safari private browsing, a full
 * quota) — memory becomes the source of truth for the rest of the session, so the switcher
 * keeps working; the choice simply isn't remembered across reloads (PR !2718 review,
 * fp:demo-role-lost-when-storage-blocked).
 */
export type PlaygroundDemoRole = 'client' | 'supplier';

const STORAGE_KEY = 'qaPlaygroundDemoRole';
const listeners = new Set<() => void>();

let memoryRole: PlaygroundDemoRole = 'client';
let storageUsable = true;

const read = (): PlaygroundDemoRole => {
  if (!storageUsable) {
    return memoryRole;
  }
  try {
    memoryRole =
      window.localStorage.getItem(STORAGE_KEY) === 'supplier' ? 'supplier' : 'client';
  } catch {
    storageUsable = false;
  }
  return memoryRole;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const usePlaygroundDemoRole = () => {
  const role = useSyncExternalStore(subscribe, read, () => 'client' as const);

  const setRole = useCallback((next: PlaygroundDemoRole) => {
    memoryRole = next;
    if (storageUsable) {
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Storage can't be written: keep the role in memory for this session only.
        storageUsable = false;
      }
    }
    listeners.forEach((listener) => listener());
  }, []);

  return [role, setRole] as const;
};

export default usePlaygroundDemoRole;
